import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import { capitalizeFirstLetter } from '../../utils/StringUtils';
import usePagination10 from '../hooks/usePagination10';
import TableContainer from '../TableContainer';
import DataTable from '../DataTable';
import PaginationBar from '../PaginationBar';
import DefaultModal from './DefaultModal';
import useDefaultModal from '../hooks/useDefaultModal';

function ItemSearchModal({ show, onClose, onAdd, demanda, demandaLote }) {
  const [item, setItem] = useState({
    grupo: '',
    classe: '',
    pdm: '',
    codigo: '',
    descricao: '',
    situacao: '',
  });
  const [catalogo, setCatalogo] = useState({ catalogoUtilizado: '' });
  const [itens, setItens] = useState([]);
  const [opcaoPesquisa, setOpcaoPesquisa] = useState('DESCRICAO');
  const [isLoading, setIsLoading] = useState(false);
  const token = localStorage.getItem('token');
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  
  const totalItems = itens.length;
  const { setPagination } = usePagination10(totalItems);
  const selectedItens = itens.slice(
    setPagination.startIndex,
    setPagination.startIndex + setPagination.rowsPerPage
  );

  function handleChange(event) {
    setItem({ ...item, [event.target.name]: event.target.value.toUpperCase() });
  }

  function handleChangeCatalogo(event) {
    setCatalogo({
      ...catalogo,
      [event.target.name]: event.target.value.toUpperCase(),
    });
  }

  // Normaliza os dados de acordo com o catálogo
  const normalizeItemData = (item) => {
    if (catalogo.catalogoUtilizado === 'E-FISCO/PE') {
      return {
        grupo: item.codigoGrupo,
        classe: item.codigoClasse,
        pdm: item.pdm || null,
        codigo: item.codigoItem,
        descricao: item.descricaoItem,
      };
    } else if (catalogo.catalogoUtilizado === 'CATSER') {
      return {
        grupo: item.codigoGrupo,
        classe: item.codigoClasse,
        pdm: null,
        codigo: item.codigoServico,
        descricao: item.nomeServico,
      };
    } else {
      // CATMAT (padrão)
      return {
        grupo: item.codigoGrupo,
        classe: item.codigoClasse,
        pdm: item.codigoPdm,
        codigo: item.codigoItem,
        descricao: item.descricaoItem,
      };
    }
  };

  // Destaca os termos pesquisados no texto
  const highlightText = (text, searchTerms) => {
    if (!text || !searchTerms) return text;
    
    const normalizeString = (str) => {
      return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    };
    
    const terms = searchTerms.split(/\s+/).filter(t => t.length >= 3);
    if (terms.length === 0) return text;
    
    // Cria regex escapando caracteres especiais e normalizando para ignorar acentos
    const escapedTerms = terms.map(term => {
      const normalized = normalizeString(term);
      return normalized.split('').map(char => {
        // Para cada caractere, cria um pattern que aceita versões com/sem acento
        const accents = {
          'a': '[aáàâãäåāăą]',
          'e': '[eéèêëēėę]',
          'i': '[iíìîïīįı]',
          'o': '[oóòôõöøōő]',
          'u': '[uúùûüūůű]',
          'c': '[cç]',
          'n': '[nñń]'
        };
        return accents[char] || char;
      }).join('');
    });
    
    const regex = new RegExp(`(${escapedTerms.join('|')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => {
      const isHighlight = terms.some(term => 
        normalizeString(part) === normalizeString(term)
      );
      return isHighlight ? (
        <mark key={index} style={{ backgroundColor: '#ffeb3b', padding: '0 2px' }}>
          {part}
        </mark>
      ) : (
        <span key={index}>{part}</span>
      );
    });
  };

  function handleAdd(p) {
    const normalized = normalizeItemData(p);
    
    const newDemandaItem = {
      cnpj: demanda.cnpj,
      demandaNum: demanda.num || demanda.demandaNum,
      loteNum: demandaLote?.numLote || demandaLote?.id,
      tipo: demanda.tipo,
      grupo: normalized.grupo,
      classe: normalized.classe,
      pdm: normalized.pdm,
      item: normalized.codigo,
      descricao: normalized.descricao,
      unidade: '',
      quantidade: '',
      vlr_unit: '',
      categoria: demanda.tipo === 'MATERIAL' ? 'Material' : 'Serviço',
      catalogoUtilizado: catalogo.catalogoUtilizado,
      tabela: catalogo.catalogoUtilizado + '_' + opcaoPesquisa,
    };

    if (onAdd) {
      onAdd(newDemandaItem);
    }
  }

  const getCatalogoOptions = () => {
    if (demanda.tipo === 'MATERIAL') {
      return [
        { value: '', label: 'Selecione...' },
        { value: 'CATMAT', label: 'CATMAT' },
        { value: 'E-FISCO/PE', label: 'E-FISCO PE' },
      ];
    } else if (demanda.tipo === 'SERVIÇO') {
      return [
        { value: '', label: 'Selecione...' },
        { value: 'CATSER', label: 'CATSER' },
        { value: 'E-FISCO/PE', label: 'E-FISCO PE' },
      ];
    } else {
      return [{ value: '', label: 'Selecione...' }];
    }
  };

  function handleOpcaoPesquisaChange(event) {
    setOpcaoPesquisa(event.target.value);
  }

  const catalogoOptions = getCatalogoOptions();

  useEffect(() => {
    setItens([]);
    setItem({ ...item, descricao: '' });
  }, [opcaoPesquisa, catalogo.catalogoUtilizado]);

  useEffect(() => {
    setOpcaoPesquisa('DESCRICAO');
  }, [catalogo.catalogoUtilizado]);

  // Limpa os dados quando o modal é fechado e define catálogo padrão quando aberto
  useEffect(() => {
    if (!show) {
      setItem({
        grupo: '',
        classe: '',
        pdm: '',
        codigo: '',
        descricao: '',
        situacao: '',
      });
      setCatalogo({ catalogoUtilizado: '' });
      setItens([]);
      setOpcaoPesquisa('DESCRICAO');
    } else {
      // Define catálogo padrão ao abrir o modal
      const catalogoPadrao = demanda.tipo === 'MATERIAL' ? 'CATMAT' : 'CATSER';
      setCatalogo({ catalogoUtilizado: catalogoPadrao });
    }
  }, [show, demanda.tipo]);

  const validateFields = () => {
    let errorMessages = [];

    if (!catalogo.catalogoUtilizado) {
      errorMessages.push('Selecione o Catálogo Utilizado!');
    }
    if (!item.descricao) {
      errorMessages.push(
        `Preencha a descrição ou o código do ${capitalizeFirstLetter(
          demanda.tipo
        )} para a pesquisa!`
      );
    }

    if (errorMessages.length > 0) {
      showDefaultModal('error', errorMessages.join('\n'));
      return false;
    }
    return true;
  };

  const handleFind = () => {
    if (!validateFields()) {
      return;
    }
    setIsLoading(true);

    // Determina o endpoint base de acordo com o catálogo
    let baseUrl = '';
    if (catalogo.catalogoUtilizado === 'CATMAT') {
      baseUrl = API_BASE_URL.CATMAT;
    } else if (catalogo.catalogoUtilizado === 'CATSER') {
      baseUrl = API_BASE_URL.CATSER;
    } else if (catalogo.catalogoUtilizado === 'E-FISCO/PE') {
      baseUrl = API_BASE_URL.EFISCO_PE;
    }

    const url = `${baseUrl}/search`;

    axios
      .get(url, {
        params: { 
          descricao: item.descricao, 
          tipo: opcaoPesquisa,
          tipoMaterial: demanda.tipo 
        },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => {
        setItens(result.data);
      })
      .catch(error => {
        console.error('Erro ao buscar itens:', error);
        showDefaultModal('error', 'Erro ao buscar itens');
      })
      .finally(() => setIsLoading(false));
  };

  const handleKeyDown = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleFind();
    }
  };

  if (!show) return null;

  return (
    <>
      <div
        className="modal show d-block"
        tabIndex="-1"
        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      >
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '90vw', height: '85vh' }}>
          <div className="modal-content" style={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: '0.5rem', overflow: 'hidden' }}>
            <div 
              className="modal-header text-white"
              style={{ 
                background: 'linear-gradient(45deg, rgb(19, 72, 156) 0%, rgba(19, 72, 156, 1) 100%)',
                padding: '0.35rem 0.75rem',
                border: 'none',
                flexShrink: 0,
              }}
            >
              <div className="d-flex align-items-center justify-content-between" style={{ width: '100%' }}>
                <h6 className="modal-title mb-0" style={{ fontSize: '0.80rem', lineHeight: 1 }}>
                  <i className="bi bi-search me-2" />
                  Pesquisa de Itens
                </h6>
                <div className="d-flex align-items-center gap-2">
                  <button
                    className="btn btn-link text-white p-0 mb-0"
                    onClick={onClose}
                    title="Fechar"
                    style={{ fontSize: '1rem', textDecoration: 'none' }}
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
              </div>
            </div>
            <div className="modal-body" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {/* Barra de filtros e pesquisa */}
              <div className="bg-light border rounded shadow-sm py-2 px-2 mb-3">
                <div
                  className="d-flex flex-wrap align-items-center gap-3"
                  style={{ rowGap: '0.5rem' }}
                >
                  <div className="d-flex align-items-center">
                    <label className="form-label me-2 mb-0 small text-muted">
                      Catálogo:
                    </label>
                    <select
                      className="form-select form-select-sm"
                      style={{ width: '140px' }}
                      value={catalogo.catalogoUtilizado}
                      onChange={handleChangeCatalogo}
                      name="catalogoUtilizado"
                    >
                      {catalogoOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <label className="form-label me-2 mb-0 small text-muted">
                      Tipo:
                    </label>
                    <div className="d-flex gap-2 flex-wrap">
                      <div className="form-check form-check-inline mb-0">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="opcaoPesquisa"
                          id="findDescricao"
                          value="DESCRICAO"
                          checked={opcaoPesquisa === 'DESCRICAO'}
                          onChange={handleOpcaoPesquisaChange}
                        />
                        <label
                          className="form-check-label small"
                          htmlFor="findDescricao"
                        >
                          Descrição
                        </label>
                      </div>
                      <div className="form-check form-check-inline mb-0">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="opcaoPesquisa"
                          id="findCodigo"
                          value="CODIGO"
                          checked={opcaoPesquisa === 'CODIGO'}
                          onChange={handleOpcaoPesquisaChange}
                        />
                        <label
                          className="form-check-label small"
                          htmlFor="findCodigo"
                        >
                          Código
                        </label>
                      </div>
                      {catalogo.catalogoUtilizado === 'CATMAT' && (
                        <div className="form-check form-check-inline mb-0">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="opcaoPesquisa"
                            id="findPdm"
                            value="PDM"
                            checked={opcaoPesquisa === 'PDM'}
                            onChange={handleOpcaoPesquisaChange}
                          />
                          <label
                            className="form-check-label small"
                            htmlFor="findPdm"
                          >
                            PDM
                          </label>
                        </div>
                      )}
                      <div className="form-check form-check-inline mb-0">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="opcaoPesquisa"
                          id="findClasse"
                          value="CLASSE"
                          checked={opcaoPesquisa === 'CLASSE'}
                          onChange={handleOpcaoPesquisaChange}
                        />
                        <label
                          className="form-check-label small"
                          htmlFor="findClasse"
                        >
                          Classe
                        </label>
                      </div>
                      <div className="form-check form-check-inline mb-0">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="opcaoPesquisa"
                          id="findGrupo"
                          value="GRUPO"
                          checked={opcaoPesquisa === 'GRUPO'}
                          onChange={handleOpcaoPesquisaChange}
                        />
                        <label
                          className="form-check-label small"
                          htmlFor="findGrupo"
                        >
                          Grupo
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="input-group flex-grow-1" style={{ minWidth: '250px', maxWidth: '400px' }}>
                    <input
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                      value={item.descricao}
                      name="descricao"
                      type="text"
                      className="form-control form-control-sm"
                      placeholder={`Digite a descrição ou código do ${capitalizeFirstLetter(
                        demanda.tipo
                      )}`}
                    />
                    <button
                      type="button"
                      onClick={handleFind}
                      className="btn btn-primary btn-sm"
                      title="Pesquisar"
                    >
                      <i className="bi bi-search" />
                    </button>
                  </div>
                </div>
              </div>

              <TableContainer isLoading={isLoading}>
                <DataTable style={{ fontSize: '0.8rem' }} head={
                    <tr>
                      <th
                        scope="col"
                        className="text-center align-middle"
                        style={{ minWidth: '80px' }}
                      >
                        Grupo
                      </th>
                      <th
                        scope="col"
                        className="text-center align-middle"
                        style={{ minWidth: '80px' }}
                      >
                        Classe
                      </th>
                      {catalogo.catalogoUtilizado === 'CATMAT' && (
                        <th
                          scope="col"
                          className="text-center align-middle"
                          style={{ minWidth: '80px' }}
                        >
                          PDM
                        </th>
                      )}
                      <th
                        scope="col"
                        className="text-center align-middle"
                        style={{ minWidth: '100px' }}
                      >
                        Código
                      </th>
                      <th scope="col" className="text-center align-middle" style={{ minWidth: '300px' }}>
                        Descrição
                      </th>
                      <th
                        scope="col"
                        className="text-center align-middle"
                        style={{ minWidth: '120px', width: '120px' }}
                      >
                        Ação
                      </th>
                    </tr>
                  }>
                  <tbody>
                    {!isLoading && selectedItens.length > 0 ? (
                      selectedItens.map(i => {
                        const normalized = normalizeItemData(i);
                        return (
                          <tr key={`${normalized.grupo}-${normalized.classe}-${normalized.codigo}`}>
                            <td className="text-center">{normalized.grupo}</td>
                            <td className="text-center">{normalized.classe}</td>
                            {catalogo.catalogoUtilizado === 'CATMAT' && (
                              <td className="text-center">{normalized.pdm || '-'}</td>
                            )}
                            <td className="text-center">{normalized.codigo}</td>
                            <td>
                              {opcaoPesquisa === 'DESCRICAO' 
                                ? highlightText(normalized.descricao, item.descricao)
                                : normalized.descricao
                              }
                            </td>
                            <td className="text-center">
                              <button
                                onClick={() => handleAdd(i)}
                                className="btn btn-success btn-sm"
                                title="Adicionar item"
                              >
                                <i className="bi bi-plus-circle me-1" />
                                Adicionar
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center">
                          {isLoading
                            ? 'Carregando...'
                            : 'Nenhum registro encontrado.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </DataTable>
              </TableContainer>
            </div>
            {/* <div className="modal-footer" style={{ flexShrink: 0 }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
              >
                Fechar
              </button>
            </div> */}
            <PaginationBar setPagination={setPagination}/>
          </div>
        </div>
      </div>
      <DefaultModal {...setDefaultModal} />
    </>
  );
}

export default ItemSearchModal;
